import { createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../shared/utils/api.js'
import {
  readNotificationsRequest,
  readNotificationsSuccess,
  readNotificationsFail,
} from '../../../state/redux/slices/notificationsSlice.js'

export const fetchNotifications = createAsyncThunk('notifications/fetch', async (_, { rejectWithValue }) => {
  try {
    let allNotifications = []
    let currentPage = 1
    let totalPages = 1

    do {
      const response = await api.get(`/v1/notifications`, {
        params: { page: currentPage },
      })

      const { notifications, pagination } = response.data.data

      allNotifications = [...allNotifications, ...notifications]

      totalPages = pagination.pages
      currentPage++
    } while (currentPage <= totalPages)

    return allNotifications
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const markAsRead = createAsyncThunk('notifications/read', async (notificationId, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/v1/notifications/${notificationId}/read`, {})
    const updatedNotification = response.data.data
    return updatedNotification
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const markAllAsRead = createAsyncThunk('notifications/markAllAsRead', async (_, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/v1/notifications/markAllAsRead`)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const deleteNotification = createAsyncThunk('notifications/delete', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/v1/notifications/${id}`)
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const sendDirectNotification = createAsyncThunk(
  'notifications/sendDirect',
  async (payload, { rejectWithValue }) => {
    try {
      // Check if payload is already FormData (new behavior)
      const isFormData = payload instanceof FormData
      let requestPayload = payload
      let contentType = 'application/json'

      if (!isFormData && payload.attachment && payload.attachment instanceof File) {
        // Legacy behavior: Create FormData if file is included in plain object
        const formData = new FormData()
        formData.append('title', payload.title)
        formData.append('description', payload.description)
        formData.append('type', payload.type)
        formData.append('attachment', payload.attachment)

        // Handle recipients array
        if (payload.recipients && Array.isArray(payload.recipients)) {
          payload.recipients.forEach((recipient, index) => {
            formData.append(`recipients[${index}]`, recipient)
          })
        }

        requestPayload = formData
        contentType = 'multipart/form-data'
      } else if (isFormData) {
        // Already FormData, set content type
        contentType = 'multipart/form-data'
      }

      const response = await api.post(`/v1/admin/notifications/send`, requestPayload, {
        headers:
          contentType === 'multipart/form-data'
            ? {
                'Content-Type': 'multipart/form-data',
              }
            : {
                'Content-Type': 'application/json',
              },
      })

      return response.data
    } catch (error) {
      console.error('Send direct notification error:', error)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

export const sendBulkNotifications = createAsyncThunk(
  'notifications/sendBulk',
  async (payload, { rejectWithValue }) => {
    try {
      // Create FormData if file is included
      let requestPayload = payload

      if (payload.attachment && payload.attachment instanceof File) {
        const formData = new FormData()
        formData.append('title', payload.title)
        formData.append('description', payload.description)
        formData.append('type', payload.type)
        formData.append('attachment', payload.attachment)

        // Handle recipients array
        if (payload.recipients && Array.isArray(payload.recipients)) {
          payload.recipients.forEach((recipient, index) => {
            formData.append(`recipients[${index}]`, recipient)
          })
        }

        requestPayload = formData
      }

      const response = await api.post(`/v1/admin/notifications/bulk`, requestPayload, {
        headers: {
          'Content-Type': payload.attachment instanceof File ? 'multipart/form-data' : 'application/json',
        },
      })

      return response.data
    } catch (error) {
      console.error('Send bulk notifications error:', error)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

// Send GDPR Privacy Policy Notification
export const sendGdprPrivacyPolicyNotification = createAsyncThunk(
  'notifications/sendGdprPrivacyPolicy',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/notifications/gdpr/privacy-policy', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      return response.data
    } catch (error) {
      console.error('Send GDPR privacy policy notification error:', error)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  }
)

// Legacy functions for backward compatibility
export const readNotification = notificationId => async dispatch => {
  try {
    dispatch(readNotificationsRequest())
    const response = await api.patch(`/v1/notifications/${notificationId}/read`, {})
    const updatedNotification = response.data.data
    dispatch(readNotificationsSuccess(response.data.data))
    return updatedNotification
  } catch (error) {
    dispatch(readNotificationsFail(error.response?.data?.message || error.message))
  }
}

export const markAllNotificationsAsRead = () => {
  return api.patch(`/v1/notifications/markAllAsRead`)
}

export const sendDirectNotificationLegacy = payload => {
  return api.post(`/v1/admin/notifications/send`, payload)
}

export const sendBulkNotificationsLegacy = payload => {
  return api.post(`/v1/admin/notifications/bulk`, payload)
}
